/**
 * Login Page
 * Supabase authentication with password and phone OTP support
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { useI18n } from '@/i18n';

// API Base URL for cross-origin requests (MinIO static hosting)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

type LoginMode = 'password' | 'phone';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();

  // Common state
  const [loginMode, setLoginMode] = useState<LoginMode>('password');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Password login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Phone login state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+86');
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Check if already logged in and redirect, otherwise cleanup timer on unmount
  useEffect(() => {
    // If user is already authenticated, redirect to home
    const existingToken = localStorage.getItem('admin_token');
    if (existingToken) {
      navigate('/', { replace: true });
      return;
    }

    // Note: We intentionally do NOT call signOut() here.
    // signOut on logout is handled in Sidebar.handleLogout.
    // Calling signOut here caused a race condition where the async signOut
    // would complete after signIn, destroying the newly created session.

    return () => {
      if (resendTimerRef.current) {
        clearInterval(resendTimerRef.current);
      }
    };
  }, [navigate]);

  // Verify and store session
  const verifyAndStoreSession = async (token: string, identifier: string) => {
    const response = await fetch(`${API_BASE_URL}/v1/admin/verify`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      if (response.status === 403) {
        throw new Error('Access denied: You do not have admin privileges');
      }
      throw new Error(errorData.detail || 'Failed to verify admin privileges');
    }

    const verifyData = await response.json();

    // Store session info
    localStorage.setItem('admin_token', token);
    localStorage.setItem('admin_user_id', verifyData.user_id);
    localStorage.setItem('admin_email', verifyData.email || identifier);
    localStorage.setItem('admin_username', verifyData.username || verifyData.email || identifier);

    // Get redirect path or default to home
    const redirectPath = sessionStorage.getItem('admin_redirect_after_login');
    sessionStorage.removeItem('admin_redirect_after_login');

    // Navigate to the redirect path or home
    const targetPath = redirectPath?.replace('/console', '') || '/';
    navigate(targetPath, { replace: true });
  };

  // Password login handler
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        throw new Error(authError.message);
      }

      if (!authData.session) {
        throw new Error('Login failed: No session created');
      }

      await verifyAndStoreSession(authData.session.access_token, authData.user?.email || email);
    } catch (err) {
      console.error('Password login error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format phone for Supabase
  const formatPhoneForSupabase = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    return countryCode + cleaned;
  };

  // Send OTP handler
  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const fullPhone = formatPhoneForSupabase(phoneNumber);

      const { error } = await supabase.auth.signInWithOtp({
        phone: fullPhone,
        options: { channel: 'sms' }
      });

      if (error) {
        throw new Error(error.message);
      }

      setShowOTPVerification(true);
      startResendTimer();

      // Focus first OTP input
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    } catch (err) {
      console.error('Send OTP error:', err);
      setError(err instanceof Error ? err.message : 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(60);

    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
    }

    resendTimerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          if (resendTimerRef.current) {
            clearInterval(resendTimerRef.current);
            resendTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // OTP input handlers
  const handleOTPInput = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are filled
    if (newDigits.every(d => d !== '')) {
      handleVerifyOTP(newDigits.join(''));
    }
  };

  const handleOTPKeydown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    const newDigits = [...otpDigits];
    for (let i = 0; i < digits.length && i < 6; i++) {
      newDigits[i] = digits[i];
    }
    setOtpDigits(newDigits);

    // Focus last filled input
    const focusIndex = Math.min(digits.length - 1, 5);
    otpInputRefs.current[focusIndex]?.focus();

    // Auto-submit if complete
    if (digits.length === 6) {
      handleVerifyOTP(digits);
    }
  };

  // Verify OTP handler
  const handleVerifyOTP = async (code?: string) => {
    const otpCode = code || otpDigits.join('');
    if (otpCode.length !== 6) {
      setError(t('login.enterAllDigits'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fullPhone = formatPhoneForSupabase(phoneNumber);

      const { data, error } = await supabase.auth.verifyOtp({
        phone: fullPhone,
        token: otpCode,
        type: 'sms'
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data.session) {
        throw new Error('No session created after verification');
      }

      await verifyAndStoreSession(data.session.access_token, data.user?.phone || fullPhone);
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError(err instanceof Error ? err.message : 'Invalid verification code');
      setOtpDigits(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Reset OTP form
  const resetOTPForm = () => {
    setShowOTPVerification(false);
    setOtpDigits(['', '', '', '', '', '']);
    setError(null);
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    }
    setResendTimer(0);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">{t('login.title')}</h2>
          <p className="mt-2 text-gray-600">{t('login.subtitle')}</p>
        </div>

        {/* Login Mode Toggle */}
        <div className="bg-white rounded-lg shadow-md mb-4 p-1 flex">
          <button
            onClick={() => { setLoginMode('password'); setError(null); }}
            className={`flex-1 py-2 px-4 rounded-md transition-colors font-medium ${
              loginMode === 'password' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('login.modePassword')}
          </button>
          <button
            onClick={() => { setLoginMode('phone'); setError(null); }}
            className={`flex-1 py-2 px-4 rounded-md transition-colors font-medium ${
              loginMode === 'phone' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {t('login.modePhone')}
          </button>
        </div>

        {/* Login Form Container */}
        <Card className="p-8">
          {/* Password Login Form */}
          {loginMode === 'password' && !showOTPVerification && (
            <form onSubmit={handlePasswordLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('login.email')}
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('login.emailPlaceholder')}
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('login.password')}
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.passwordPlaceholder')}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    disabled={loading}
                  >
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {showPassword ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      )}
                    </svg>
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <Button type="submit" variant="primary" className="w-full" disabled={loading}>
                {loading ? t('login.signingIn') : t('login.signIn')}
              </Button>
            </form>
          )}

          {/* Phone Login Form */}
          {loginMode === 'phone' && !showOTPVerification && (
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('login.phoneNumber')}
                </label>
                <div className="flex gap-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    disabled={loading}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="+86">+86</option>
                    <option value="+1">+1</option>
                    <option value="+852">+852</option>
                    <option value="+886">+886</option>
                    <option value="+65">+65</option>
                  </select>
                  <Input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder={t('login.phonePlaceholder')}
                    required
                    disabled={loading}
                    maxLength={11}
                    className="flex-1"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">{t('login.phoneHint')}</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={loading || phoneNumber.length < 10}
              >
                {loading ? t('login.sendingCode') : t('login.sendCode')}
              </Button>
            </form>
          )}

          {/* OTP Verification Form */}
          {showOTPVerification && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('login.verificationTitle')}</h3>
                <p className="text-sm text-gray-600">
                  {t('login.codeSentTo', { phone: `${countryCode} ${phoneNumber}` })}
                </p>
              </div>

              {/* OTP Input Boxes */}
              <div className="flex justify-center gap-2">
                {otpDigits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { otpInputRefs.current[index] = el; }}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOTPInput(index, e.target.value)}
                    onKeyDown={(e) => handleOTPKeydown(index, e)}
                    onPaste={handleOTPPaste}
                    className={`w-12 h-14 text-center text-2xl font-semibold border-2 rounded-lg transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      digit ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                    }`}
                  />
                ))}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              {/* Resend Timer */}
              <div className="text-center">
                <button
                  onClick={() => handleSendOTP({ preventDefault: () => {} } as React.FormEvent)}
                  disabled={resendTimer > 0 || loading}
                  className={`text-sm font-medium transition-colors ${
                    resendTimer > 0 || loading ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700'
                  }`}
                >
                  {resendTimer > 0 ? t('login.resendIn', { seconds: String(resendTimer) }) : t('login.resendCode')}
                </button>
              </div>

              {/* Back Button */}
              <Button variant="secondary" className="w-full" onClick={resetOTPForm}>
                {t('login.backToPhone')}
              </Button>
            </div>
          )}
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center space-y-2">
          <p className="text-sm text-gray-600">
            {t('login.footer')}
          </p>
          <a
            href="/releases.html"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Release Notes
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
